import {
  Box,
  Typography,
  Popover,
  useTheme,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useMemo, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const PRIMARY = "#0B5C8A";

// Stage colours keyed by meta-status wording — visual only.
const metaTone = (meta) => {
  const m = (meta || "").toLowerCase();
  if (m.includes("lost")) return { dot: "#D9493D", tint: "#FBEDEB", edge: "#F2CFCA", ink: "#A6342A" };
  if (m.includes("won")) return { dot: "#1A9CC2", tint: "#E4F5FA", edge: "#BCE3EF", ink: "#0E5F78" };
  if (m.includes("commit")) return { dot: "#2FA864", tint: "#E9F7EE", edge: "#C4E8D2", ink: "#1B6B41" };
  if (m.includes("negot")) return { dot: "#D6408C", tint: "#FBEAF3", edge: "#F2CDE1", ink: "#9A2F69" };
  if (m.includes("visit")) return { dot: "#E8A33D", tint: "#FDF4E1", edge: "#F3E0B4", ink: "#8A5A08" };
  if (m.includes("engag")) return { dot: "#17A398", tint: "#E6F6F5", edge: "#BFE6E3", ink: "#11645E" };
  if (m.includes("contact")) return { dot: "#6C5CE7", tint: "#EFEDFB", edge: "#D3D0F2", ink: "#4A3FBF" };
  if (m.includes("new")) return { dot: "#1F6FEB", tint: "#EAF2FB", edge: "#C9DEF3", ink: "#1B4F82" };
  return { dot: "#8494A8", tint: "#F1F4F8", edge: LINE, ink: INK_2 };
};

const LeadCountCards = ({
  leads = [],
  metaStatuses = [],
  onFilterChange,
  activeStatusFilter,
  activeSubStatusFilter, // NEW: currently active sub-status (string | null)
  onSubStatusFilterChange, // NEW: (subStatus: string | null) => void
  allLeads = [],
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredMeta, setHoveredMeta] = useState(null);
  const [expandedMeta, setExpandedMeta] = useState(null); // for mobile accordion
  const theme = useTheme();
  const isMobileWidth = useMediaQuery(theme.breakpoints.down("sm"));
  const isCoarsePointer =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  const isTouch = isMobileWidth || isCoarsePointer;

  const metaStatusData = useMemo(() => {
    const leadToMeta = metaStatuses.reduce((acc, item) => {
      acc[item.status] = item.metaLeadStatus?.status || "Unknown";
      return acc;
    }, {});

    const metaMap = {};
    allLeads.forEach((lead) => {
      const leadStatus = lead.status || "NEW";
      const metaStatus = leadToMeta[leadStatus] || "Unknown";

      if (!metaMap[metaStatus]) {
        metaMap[metaStatus] = {
          total: 0,
          statuses: {},
        };
      }
      metaMap[metaStatus].statuses[leadStatus] =
        metaMap[metaStatus].statuses[leadStatus] || 0;
    });

    leads.forEach((lead) => {
      const leadStatus = lead.status || "NEW";
      const metaStatus = leadToMeta[leadStatus] || "Unknown";

      if (!metaMap[metaStatus]) {
        metaMap[metaStatus] = { total: 0, statuses: {} };
      }

      metaMap[metaStatus].total += 1;
      metaMap[metaStatus].statuses[leadStatus] =
        (metaMap[metaStatus].statuses[leadStatus] || 0) + 1;
    });

    return Object.entries(metaMap).reduce((acc, [meta, data]) => {
      acc[meta] = {
        total: data.total,
        statuses: Object.entries(data.statuses).map(([status, count]) => ({
          status,
          count,
        })),
      };
      return acc;
    }, {});
  }, [leads, allLeads, metaStatuses]);

  // ── Desktop hover handlers ──────────────────────────────────────────────
  const handleMouseEnter = (e, meta) => {
    if (isTouch) return;
    setAnchorEl(e.currentTarget);
    setHoveredMeta(meta);
  };

  const handleMouseLeave = () => {
    if (isTouch) return;
    setAnchorEl(null);
    setHoveredMeta(null);
  };

  // ── Meta card click ─────────────────────────────────────────────────────
  const handleMetaClick = (e, meta) => {
    const next = activeStatusFilter === meta ? meta : meta;
    onFilterChange?.(next);
    // Clear sub-status when meta changes
    onSubStatusFilterChange?.(null);
    // Toggle mobile accordion
    if (isTouch) {
      setExpandedMeta((prev) => (prev === meta ? null : meta));
    }
  };

  // ── Sub-status click (desktop popover + mobile chip) ────────────────────
  const handleSubStatusClick = (e, meta, subStatus) => {
    e.stopPropagation();

    const isSame =
      activeSubStatusFilter === subStatus && activeStatusFilter === meta;

    if (isSame) {
      onFilterChange?.(null);
      onSubStatusFilterChange?.(null);
    } else {
      onFilterChange?.(meta);
      onSubStatusFilterChange?.(subStatus);
    }

    if (!isTouch) {
      setAnchorEl(null);
      setHoveredMeta(null);
    }
  };

  const totalOn = activeStatusFilter === null;
  const entries = Object.entries(metaStatusData);
  const barTotal = entries.reduce((s, [, d]) => s + d.total, 0);

  const chipBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    height: 32,
    px: 1.5,
    borderRadius: "9px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontFamily: FONT,
    fontSize: 13,
    userSelect: "none",
    transition: "background .15s, border-color .15s",
  };
  const expandedEntry = entries.find(([m]) => m === expandedMeta);

  return (
    <Box
      sx={{
        mt: 1.5,
        p: "14px 16px",
        background: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: "14px",
        fontFamily: FONT,
        color: INK,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
        <Typography sx={{ ...labelSx, color: MUTED }}>Pipeline</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MUTED }}>
          {leads.length} leads · {entries.length} stage{entries.length === 1 ? "" : "s"}
        </Typography>
      </Box>

      {/* Stacked bar — one segment per stage, width = share of leads */}
      <Box sx={{ display: "flex", gap: "3px", height: 10, mb: 1.5 }}>
        {barTotal > 0 ? (
          entries.map(([meta, data]) =>
            data.total > 0 ? (
              <Box
                key={meta}
                title={`${meta}: ${data.total}`}
                onClick={(e) => handleMetaClick(e, meta)}
                sx={{
                  flex: data.total,
                  minWidth: 4,
                  borderRadius: "3px",
                  cursor: "pointer",
                  background: metaTone(meta).dot,
                  opacity: activeStatusFilter && activeStatusFilter !== meta ? 0.35 : 1,
                  transition: "opacity .15s",
                }}
              />
            ) : null,
          )
        ) : (
          <Box sx={{ flex: 1, borderRadius: "3px", background: "#EDF1F5" }} />
        )}
      </Box>

      {/* Stage chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {/* TOTAL LEADS */}
        <Box
          sx={{
            ...chipBase,
            background: totalOn ? INK : "#FFFFFF",
            color: totalOn ? "#FFFFFF" : INK_2,
            border: `1px solid ${totalOn ? INK : "#D5DEE7"}`,
            fontWeight: totalOn ? 700 : 500,
            "&:hover": { borderColor: totalOn ? INK : "#B4C6DA" },
          }}
          onClick={() => {
            onFilterChange?.(null);
            onSubStatusFilterChange?.(null);
            setExpandedMeta(null);
          }}
        >
          All
          <span style={{ fontFamily: MONO, fontSize: 12, opacity: totalOn ? 0.75 : 1, color: totalOn ? "#FFFFFF" : MUTED }}>
            {leads.length}
          </span>
        </Box>

        {entries.map(([meta, data]) => {
          const isOn = activeStatusFilter === meta;
          const tone = metaTone(meta);
          const pct = allLeads.length ? (data.total * 100) / allLeads.length : 0;

          return (
            <Box
              key={meta}
              sx={{
                ...chipBase,
                background: isOn ? tone.tint : "#FFFFFF",
                color: isOn ? tone.ink : INK_2,
                border: `1px solid ${isOn ? tone.dot : "#D5DEE7"}`,
                fontWeight: isOn ? 700 : 500,
                "&:hover": { borderColor: tone.dot },
              }}
              onMouseEnter={(e) => handleMouseEnter(e, meta)}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => handleMetaClick(e, meta)}
            >
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: tone.dot, flex: "0 0 7px" }} />
              {meta}
              <span style={{ fontFamily: MONO, fontSize: 12, color: isOn ? tone.ink : MUTED }}>{data.total}</span>
              {isOn && activeSubStatusFilter && (
                <span style={{ fontSize: 11.5, fontWeight: 600, color: tone.ink }}>· {activeSubStatusFilter}</span>
              )}
              {isTouch && (
                expandedMeta === meta ? <KeyboardArrowUpIcon sx={{ fontSize: 16, ml: -0.25 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 16, ml: -0.25 }} />
              )}

              {!isTouch && (
                <Popover
                  open={hoveredMeta === meta}
                  anchorEl={anchorEl}
                  onClose={() => {
                    setHoveredMeta(null);
                    setAnchorEl(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  disableRestoreFocus
                  sx={{ pointerEvents: "none" }}
                  slotProps={{
                    paper: {
                      sx: {
                        pointerEvents: "auto",
                        mt: 0.75,
                        borderRadius: "14px",
                        border: `1px solid ${LINE}`,
                        boxShadow: "0 16px 36px -12px rgba(15,27,42,0.28)",
                      },
                      onMouseLeave: handleMouseLeave,
                    },
                  }}
                >
                  <Box sx={{ px: 1.75, pt: 1.25, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: tone.ink }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: tone.dot }} />
                      {meta}
                    </span>
                    <span style={{ fontFamily: MONO, letterSpacing: 0, color: MUTED }}>{pct.toFixed(1)}%</span>
                  </Box>
                  <Box
                    sx={{
                      p: 1.25,
                      pt: 0.75,
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                      gap: 1,
                      maxWidth: 400,
                    }}
                  >
                    {data.statuses.map((s) => {
                      const isSubActive = activeSubStatusFilter === s.status;
                      return (
                        <Box
                          key={s.status}
                          sx={{
                            ...innerCardStyle,
                            cursor: "pointer",
                            background: isSubActive ? tone.tint : "#FFFFFF",
                            color: INK,
                            border: `1px solid ${isSubActive ? tone.dot : LINE}`,
                            "&:hover": {
                              background: isSubActive ? tone.tint : "#F7F9FC",
                              borderColor: tone.dot,
                            },
                          }}
                          onClick={(e) => handleSubStatusClick(e, meta, s.status)}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", fontFamily: FONT, fontSize: 12, fontWeight: isSubActive ? 700 : 500, color: isSubActive ? tone.ink : INK_2, lineHeight: 1.3 }}
                          >
                            {s.status}
                          </Typography>
                          <Typography fontWeight={600} sx={{ fontFamily: MONO, fontSize: 15, color: INK, mt: 0.25 }}>
                            {s.count}{" "}
                            <span style={{ fontSize: "11px", color: MUTED }}>
                              ({allLeads.length ? ((s.count * 100) / allLeads.length).toFixed(2) : "0.00"}%)
                            </span>
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Popover>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Mobile: sub-statuses of the tapped stage, below the chip row */}
      {isTouch && (
        <Collapse in={!!expandedEntry}>
          {expandedEntry && (
            <Box sx={{ mt: 1.25, border: `1px solid ${LINE}`, borderRadius: "12px", overflow: "hidden", background: "#FFFFFF" }}>
              {expandedEntry[1].statuses.map((s, idx, arr) => {
                const meta = expandedEntry[0];
                const tone = metaTone(meta);
                const isSubActive = activeSubStatusFilter === s.status;
                return (
                  <Box
                    key={s.status}
                    onClick={(e) => handleSubStatusClick(e, meta, s.status)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1.5,
                      minHeight: 44,
                      cursor: "pointer",
                      borderBottom: idx < arr.length - 1 ? "1px solid #F1F4F8" : "none",
                      bgcolor: isSubActive ? tone.tint : "#FFFFFF",
                      "&:active": { bgcolor: "#F4F7FA" },
                    }}
                  >
                    <Typography fontSize={13} fontWeight={isSubActive ? 700 : 500} color={isSubActive ? tone.ink : INK} sx={{ fontFamily: FONT }}>
                      {s.status}
                    </Typography>
                    <Box sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, px: 1, py: 0.25, borderRadius: "20px", bgcolor: isSubActive ? "#FFFFFF" : "#F1F4F8", color: isSubActive ? tone.ink : MUTED, minWidth: 28, textAlign: "center" }}>
                      {s.count}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
};

const cardStyle = {
  border: `1px solid ${LINE}`,
  p: "12px 14px",
  borderRadius: "14px",
  minWidth: 0,
  textAlign: "left",
  transition: "background .15s, border-color .15s, box-shadow .15s",
  "&:hover": { borderColor: "#B4C6DA" },
};

const labelSx = {
  display: "block",
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  lineHeight: 1.4,
};

const countSx = {
  fontFamily: MONO,
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: "-0.03em",
  lineHeight: 1.25,
  mt: 0.5,
};

const innerCardStyle = {
  border: `1px solid ${LINE}`,
  borderRadius: "10px",
  p: "8px 10px",
  textAlign: "left",
  backgroundColor: "#FFFFFF",
  transition: "all 0.15s",
};

export default LeadCountCards;
